import type { ReactElement } from 'react';
import type { ChannelDto } from '@gr/core/distribution/channels/dto';
import { useT } from '../../../../app/di';
import { Button } from '../../../../ui-kit/Button/Button';
import { FormAlert, FormSelectField, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { useChannelOptions } from '../../channels';

export interface RecordValues {
  channelId: string;
  /** В поясе блога, «ГГГГ-ММ-ДДTчч:мм». */
  publishedAt: string;
  url: string;
}

export interface RecordPublicationFormProps {
  channels: readonly ChannelDto[];
  /** Новый выход — канал и время по умолчанию; правка — текущие значения записи. */
  defaultValues: RecordValues;
  submitLabel: string;
  hint?: string;
  onSubmit(values: RecordValues): Promise<ServerOutcome>;
  /** Правка: закрыть форму без сохранения. */
  onCancel?(): void;
}

/** PUB-UC-8, PUB-UC-9: записать выход, сделанный вне системы, или исправить запись о выходе. */
export function RecordPublicationForm({ channels, defaultValues, submitLabel, hint, onSubmit, onCancel }: RecordPublicationFormProps): ReactElement {
  const { t } = useT();
  const { form, submit } = useServerForm<RecordValues>({
    defaultValues,
    fields: ['channelId', 'publishedAt', 'url'],
    onSubmit,
  });
  // CHN-UC-6: платформа канала — в подписи варианта.
  const channelOptions = useChannelOptions(channels);
  const disabled = form.formState.isSubmitting;
  return (
    <form className="u-stack" onSubmit={submit} noValidate>
      {hint ? <p className="u-muted u-small">{hint}</p> : null}
      <FormAlert form={form} />
      <FormSelectField control={form.control} name="channelId" label={t('publishing.history.channel')} options={channelOptions} disabled={disabled} />
      <FormTextField control={form.control} name="publishedAt" label={t('publishing.history.at')} type="datetime-local" disabled={disabled} />
      <FormTextField control={form.control} name="url" label={t('publishing.history.url')} type="url" disabled={disabled} />
      <div className="u-row">
        <Button type="submit" variant="primary" loading={disabled}>
          {submitLabel}
        </Button>
        {onCancel ? (
          <Button variant="ghost" disabled={disabled} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
